import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid } from '@mui/material'
import { Preview } from '@refera/ui-web'

import { Controller, useFormContext } from 'react-hook-form'
import { Attachments } from '_/components/refera-ui'

import useStyles from './styles'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'

const NAME = 'problemMedia'
const defaultPreviewState = {
  visible: false,
  selectedIndex: undefined,
}

export function AttachmentsField() {
  const styles = useStyles()
  const { control, watch, setValue } = useFormContext()
  const { addProblemReadOnly } = useBudgetForm()

  const problemMedia = watch(NAME, [])
  const [attachments, setAttachments] = useState([])
  const [previewState, setPreviewState] = useState(defaultPreviewState)

  const images = useMemo(
    () => (attachments?.length > 0 ? attachments?.filter(file => !isFilePDF(file)) || [] : []),
    [attachments, isFilePDF]
  )

  const pdfs = useMemo(
    () => (attachments?.length > 0 ? attachments.filter(file => isFilePDF(file)) || [] : []),
    [attachments, isFilePDF]
  )

  const handleAttachmentsChange = useCallback(
    files => {
      setAttachments(prev => {
        const updatedFiles = [...prev, ...files]
        return updatedFiles
      })

      setValue(NAME, [...problemMedia, ...files])
    },
    [setValue, problemMedia]
  )

  const handleRemoveAttachments = useCallback(
    (indexToRemove, isPdf) => {
      const files = isPdf ? pdfs : images
      const remainingFiles = isPdf ? images : pdfs

      let updatedAttachments = files.filter((file, index) => index !== indexToRemove)
      updatedAttachments = [...remainingFiles, ...updatedAttachments]

      setAttachments(updatedAttachments)
      setValue(NAME, updatedAttachments)
    },
    [setValue, pdfs, images, attachments, setAttachments]
  )

  function isFilePDF(file) {
    return file instanceof File ? file.type === 'application/pdf' : file.file?.includes('.pdf')
  }

  const PreviewComponent = useMemo(() => {
    if (images?.length === 0 || !previewState.visible) return null

    return (
      <Preview
        open={previewState.visible}
        onClose={() => setPreviewState(() => defaultPreviewState)}
        selectedIndex={previewState.selectedIndex}
        images={images?.map((item, index) =>
          item?.id
            ? {
                id: item.id,
                url: item?.file,
              }
            : {
                id: index,
                url: URL.createObjectURL(item),
              }
        )}
      />
    )
  }, [previewState, images, defaultPreviewState])

  useEffect(() => {
    if (problemMedia?.length > 0) {
      setAttachments(
        problemMedia.map(file => {
          if (file instanceof File) {
            return file
          }
          return {
            file: file?.picture || file?.file,
            id: file?.id,
          }
        })
      )
    } else {
      setAttachments([])
    }
  }, [problemMedia])

  return (
    <Grid className={styles.container}>
      {PreviewComponent}
      <Controller
        name={NAME}
        control={control}
        defaultValue={attachments || []}
        render={() => (
          <>
            <Attachments
              label="Fotos e vídeos do problema"
              files={images || []}
              onDrop={handleAttachmentsChange}
              onRemoveItem={index => handleRemoveAttachments(index, false)}
              onItemClick={index => {
                setPreviewState(() => ({
                  visible: true,
                  selectedIndex: index,
                }))
              }}
              accept={{
                'image/jpeg': [
                  '.jpeg',
                  '.png',
                  '.jpg',
                  '.bmp',
                  '.webp',
                  '.mkv',
                  '.mp4',
                  '.mov',
                  '.avi',
                  '.m4v',
                  '.mpg',
                  '.mpeg',
                  '.wmv',
                  '.webm',
                ],
              }}
              readOnly={addProblemReadOnly}
            />
            <Attachments
              label="Arquivos PDF"
              content="files"
              files={pdfs || []}
              onDrop={handleAttachmentsChange}
              onRemoveItem={index => handleRemoveAttachments(index, true)}
              onItemClick={index => window.open(pdfs?.[index]?.file)}
              accept={{ 'application/pdf': ['.pdf'] }}
              readOnly={addProblemReadOnly}
            />
          </>
        )}
      />
    </Grid>
  )
}
